import React, { FC, Fragment, ReactNode, useContext, useState } from 'react'
import {
	CorporateFare,
	DesktopMacRounded,
	HelpOutline,
	MeetingRoom,
	Menu,
	NotificationsActiveOutlined,
	Person,
	Search,
	Settings,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { Button, IconButton, OptionsMenu } from '../ui'
import type { CurrentUserObject } from '../../lib/api/types/user.interfaces'
import cn from '../../lib/utils/cn'
import DownloadAuraClientModal from '../modals/user/DownloadAuraClientModal'
import SearchModal from '../modals/workspace/SearchModal'
import { Features, useBilling } from '../../context/billingContext'
import { useAuth } from '../../hooks'
import { useUnreadInboxCount } from '../../hooks/api/inboxHooks'
import AppContext from '../../context/appContext'
import { postMessageToNative } from '../../lib/utils/nativeWebView'

interface NavbarProps {
	isAdmin?: boolean
	isOwner?: boolean
	currentUser: CurrentUserObject | null
}

const Navbar: FC<NavbarProps> = ({ currentUser }) => {
	const { hasFeature } = useBilling()
	const { isAuthenticated, logout, login } = useAuth()

	const { data: unreadInboxCountData } = useUnreadInboxCount(
		{},
		{ enabled: isAuthenticated }
	)
	const unreadInboxCount = unreadInboxCountData?.data || 0

	const navigate = useNavigate()
	const { isSidebarOpen, setIsSidebarOpen, isApp } = useContext(AppContext)
	const [searchModalOpen, setSearchModalOpen] = useState(false)
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
	const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)

	const helpUrl = process.env.REACT_APP_HELP_URL

	const handleHelpClick = () => {
		if (!helpUrl) return

		if (isApp) {
			postMessageToNative('OPEN_EXTERNAL_LINK', { url: helpUrl })
		} else {
			window.open(helpUrl, '_blank', 'noopener,noreferrer')
		}
	}

	return (
		<Fragment>
			<header className="p-5 w-full h-20 box-border flex items-center justify-between border-b border-stroke-light dark:border-stroke-light-dark  bg-white dark:bg-background-dark">
				<div
					className={cn(
						'flex items-center gap-5 transition-transform duration-300 ease-in-out'
					)}
				>
					<IconButton
						className="hidden max-lg:flex"
						variant="transparent"
						icon={<Menu />}
						onClick={() => setIsSidebarOpen(!isSidebarOpen)}
					/>
					{isAuthenticated && !!currentUser?.organization && (
						<div className="flex items-center gap-4">
							<ImageWrapper
								mainImage={currentUser?.organization?.logo}
								mainImageAlt="organization-logo"
								placeholder={
									<CorporateFare className="fill-text-secondary dark:fill-text-main-dark" />
								}
							/>
							<p className="font-bold text-text-main text-2xl capitalize max-sm:hidden block dark:text-text-main-dark truncate max-w-[500px]">
								{currentUser?.organization?.name}
							</p>
						</div>
					)}
				</div>
				{isAuthenticated ? (
					<div className="flex items-center gap-3">
						{hasFeature(Features.Collaboration) && (
							<div className="relative">
								<IconButton
									variant="secondary"
									className="group"
									icon={
										<NotificationsActiveOutlined className="group-hover:animate-shake dark:fill-stroke-light" />
									}
									onClick={() => {
										navigate('/my-space/inbox')
									}}
									tooltip={`You have ${unreadInboxCount} new notification${unreadInboxCount > 1 ? 's' : ''
										}`}
								/>
								{unreadInboxCount > 0 && (
									<span className="absolute select-none flex items-center justify-center w-5 h-5 -top-2 -right-2 text-[10px] ] font-medium bg-accent text-white rounded-full text-center">
										{unreadInboxCount > 99 ? '99+' : unreadInboxCount}
									</span>
								)}
							</div>
						)}
						{hasFeature(Features.Nlp) && (
							<IconButton
								variant="secondary"
								icon={
									<Search className="fill-text-main dark:fill-stroke-light" />
								}
								onClick={() => {
									// if (isNotespage) {
									// 	setNotesSearchModalOpen(true)
									// } else {
									setSearchModalOpen(true)
									// }
								}}
							/>
						)}
						<Button
							className={cn(
								'hidden lg:flex dark:text-text-main-dark',
								isDownloadModalOpen && 'bg-background-light'
							)}
							classNameContent="gap-2.5"
							variant="secondary"
							leftIcon={
								<DesktopMacRounded className="fill-text-main w-5 h-5 dark:fill-text-main-dark" />
							}
							label="Install AuraDrive"
							onClick={() => setIsDownloadModalOpen(true)}
						/>

						<div className="relative ">
							<IconButton
								variant="secondary"
								icon={
									<ImageWrapper
										mainImage={currentUser?.profile?.avatar}
										placeholder={<Person className="fill-text-secondary" />}
										wrapperClassName="p-1 bg-white dark:bg-background-dark"
										className="rounded-md overflow-hidden"
									/>
								}
								onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
							/>
							<OptionsMenu
								isOpen={isUserMenuOpen}
								onClose={() => setIsUserMenuOpen(false)}
								directionX="left"
								directionY="bottom"
								className="right-0 top-full mt-1"
								labelClassName="text-text-main font-medium text-base dark:text-text-main-dark"
								itemClassName="gap-2.5 flex items-center dark:text-text-main-dark text-text-main dark:hover:bg-background-dark-hover"
								items={[
									{
										icon: <Settings className="dark:fill-text-main-dark" />,
										label: 'Settings',
										onClick: () => navigate('/settings/user'),
									},
									...(helpUrl
										? [
											{
												icon: (
													<HelpOutline className="dark:fill-text-main-dark" />
												),
												label: 'Help',
												onClick: handleHelpClick,
											},
										]
										: []),
									{
										icon: <MeetingRoom className="dark:fill-text-main-dark" />,
										label: 'Log out',
										onClick: () => logout(),
									},
								]}
							/>
						</div>
					</div>
				) : (
					<>
						<Button
							variant="secondary"
							label="Log In with auth0"
							onClick={() => login()}
						/>
						{/* TEMPORARILY DISABLED: AD auth */}
						{/* <Button
							variant="secondary"
							label="Log In with AD"
							onClick={() => login('azure')}
						/> */}
					</>
				)}
			</header>
			<DownloadAuraClientModal
				isOpen={isDownloadModalOpen}
				onClose={() => setIsDownloadModalOpen(false)}
			/>

			<SearchModal
				open={searchModalOpen}
				onClose={() => setSearchModalOpen(false)}
			/>
		</Fragment>
	)
}

export const ImageWrapper = ({
	placeholder,
	mainImage,
	mainImageAlt,
	className,
	wrapperClassName,
}: {
	placeholder: ReactNode
	mainImage?: string | null
	mainImageAlt?: string
	className?: string
	wrapperClassName?: string
}) => (
	<div
		className={cn(
			'w-10 h-10 flex items-center justify-center box-border rounded overflow-hidden',
			mainImage
				? 'bg-transparent'
				: 'bg-background-light dark:bg-background-light-dark rounded-lg',
			wrapperClassName
		)}
	>
		<div
			className={cn(
				'w-full h-full flex items-center justify-center',
				className
			)}
		>
			{mainImage ? (
				<img
					src={mainImage}
					alt={mainImageAlt ?? 'image-alt'}
					className="object-contain h-full w-full"
				/>
			) : (
				placeholder
			)}
		</div>
	</div>
)
export default Navbar
